import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'forms', label: 'Forms' },
  { key: 'views', label: 'Views' },
  { key: 'agents', label: 'Agents' },
  { key: 'relationships', label: 'Relationships' },
  { key: 'workflows', label: 'Workflows' },
  { key: 'security', label: 'Security' },
  { key: 'sql', label: 'SQL Schema' },
  { key: 'migration', label: 'Migration' },
];

// Safe-render utility: ensures a value is a primitive string before rendering in JSX
function safeStr(val, fallback = '—') {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(v => safeStr(v, '')).filter(Boolean).join(', ') || fallback;
  if (typeof val === 'object') {
    // Try common property names
    const tryKeys = ['name', 'label', 'title', 'description', 'value', 'text', 'displayName'];
    for (const k of tryKeys) {
      if (typeof val[k] === 'string') return val[k];
    }
    return JSON.stringify(val);
  }
  return String(val);
}

// Complexity color mapping
function complexityColor(score) {
  if (score >= 8) return 'var(--color-danger)';
  if (score >= 6) return 'var(--color-warning)';
  if (score >= 4) return '#d97706';
  if (score >= 2) return 'var(--color-success)';
  return 'var(--color-info)';
}

function complexityBadge(label) {
  const map = {
    'Very High': 'var(--color-danger)',
    'High': 'var(--color-warning)',
    'Medium': '#d97706',
    'Low': 'var(--color-success)',
    'Trivial': 'var(--color-info)',
    'very_complex': 'var(--color-danger)',
    'complex': 'var(--color-warning)',
    'moderate': '#d97706',
    'simple': 'var(--color-success)',
    'trivial': 'var(--color-info)',
  };
  return map[label] || 'var(--color-text-secondary)';
}

// ─── Tab Content Components ───────────────────────────────────────────────────

function OverviewTab({ summary, databaseInfo, results }) {
  if (!summary) return <p>No summary available.</p>;
  const dist = summary.complexityDistribution || {};

  return (
    <div className="analysis-tab-content">
      {/* Top stats grid */}
      <div className="analysis-overview-grid">
        <StatCard label="Application" value={summary.applicationName || 'Unknown'} />
        <StatCard label="Complexity" value={summary.complexityLabel || 'N/A'} color={complexityBadge(summary.complexityLabel)} />
        <StatCard label="Score" value={summary.overallComplexity ? summary.overallComplexity.toFixed(1) : '0'} sub="/10" />
        <StatCard label="Est. Effort" value={`${summary.estimatedEffortDays || 0}d`} sub={`(${summary.estimatedEffortWeeks || 0} weeks)`} />
        <StatCard label="Forms" value={summary.totalForms || 0} />
        <StatCard label="Views" value={summary.totalViews || 0} />
        <StatCard label="Agents" value={summary.totalAgents || 0} />
        <StatCard label="Fields" value={summary.totalFields || 0} />
        <StatCard label="Relationships" value={summary.totalRelationships || 0} />
        <StatCard label="Domain" value={summary.detectedDomain || 'Unknown'} sub={summary.domainConfidence ? `${Math.round(summary.domainConfidence * 100)}%` : ''} />
        <StatCard label="Workflows" value={summary.workflowCount || 0} />
        <StatCard label="Risk Level" value={summary.migrationRiskLevel || 'N/A'} color={complexityBadge(summary.migrationRiskLevel === 'high' ? 'High' : summary.migrationRiskLevel === 'medium' ? 'Medium' : 'Low')} />
      </div>

      {/* Complexity distribution */}
      {Object.keys(dist).length > 0 && (
        <div className="analysis-section">
          <h3>Complexity Distribution</h3>
          <div className="complexity-bar-chart">
            {Object.entries(dist).map(([level, count]) => (
              <div key={level} className="complexity-bar-item">
                <span className="complexity-bar-label">{level}</span>
                <div className="complexity-bar-track">
                  <div className="complexity-bar-fill" style={{
                    width: `${Math.max(5, (count / (summary.totalForms || 1)) * 100)}%`,
                    background: complexityBadge(level),
                  }} />
                </div>
                <span className="complexity-bar-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Element type breakdown */}
      {summary.elementTypeCounts && (
        <div className="analysis-section">
          <h3>Element Type Breakdown</h3>
          <div className="element-type-grid">
            {Object.entries(summary.elementTypeCounts).map(([type, count]) => (
              <div key={type} className="element-type-item">
                <span className="element-type-count">{count}</span>
                <span className="element-type-label">{type.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Database info */}
      {databaseInfo && (
        <div className="analysis-section">
          <h3>Database Information</h3>
          <div className="detail-kv-list">
            {databaseInfo.databaseTitle && <KV label="Title" value={databaseInfo.databaseTitle} />}
            {databaseInfo.replicaId && <KV label="Replica ID" value={databaseInfo.replicaId} />}
            {databaseInfo.adminServer && <KV label="Admin Server" value={databaseInfo.adminServer} />}
            {Array.isArray(databaseInfo.editors) && databaseInfo.editors.length > 0 && <KV label="Editors" value={databaseInfo.editors.map(e => safeStr(e)).join(', ')} />}
            {databaseInfo.totalEntries > 0 && <KV label="Total Entries" value={databaseInfo.totalEntries} />}
          </div>
        </div>
      )}
    </div>
  );
}

function FormsTab({ forms }) {
  const [expandedForm, setExpandedForm] = useState(null);
  if (!forms || !forms.forms || forms.forms.length === 0) return <p>No forms found in this application.</p>;

  return (
    <div className="analysis-tab-content">
      <p className="tab-summary">{forms.totalForms} forms with {forms.totalFields} total fields</p>
      <div className="element-list">
        {forms.forms.map((form, idx) => {
          const isExpanded = expandedForm === idx;
          const complexityScore = form.complexityScore || form.complexity || 0;
          return (
            <div key={idx} className={`element-card ${isExpanded ? 'expanded' : ''}`}>
              <div className="element-card-header" onClick={() => setExpandedForm(isExpanded ? null : idx)}>
                <div className="element-card-title">
                  <span className="element-card-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/></svg>
                  </span>
                  <strong>{form.name}</strong>
                  {form.alias && <span className="element-alias">({form.alias})</span>}
                </div>
                <div className="element-card-meta">
                  <span className="meta-badge">{(form.fields || []).length} fields</span>
                  <span className="meta-badge">{(form.actions || []).length} actions</span>
                  {complexityScore > 0 && (
                    <span className="meta-badge" style={{ color: complexityColor(complexityScore) }}>
                      score: {typeof complexityScore === 'number' ? complexityScore.toFixed(1) : complexityScore}
                    </span>
                  )}
                  <span className="expand-arrow">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                </div>
              </div>
              {isExpanded && (
                <div className="element-card-body">
                  {/* Fields table */}
                  {form.fields && form.fields.length > 0 && (
                    <div className="sub-section">
                      <h4>Fields ({form.fields.length})</h4>
                      <table className="mini-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Kind</th>
                            <th>Details</th>
                            <th>Formulas / Default</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.fields.map((field, fi) => (
                            <tr key={fi}>
                              <td>
                                <strong>{field.name}</strong>
                                {field.isIdField && <span className="type-chip" style={{ marginLeft: 4, fontSize: '0.65rem', background: 'var(--color-primary)', color: '#fff' }}>ID</span>}
                                {field.isForeignKey && <span className="type-chip" style={{ marginLeft: 4, fontSize: '0.65rem', background: 'var(--color-info)', color: '#fff' }}>FK</span>}
                                {field.isAuditField && <span className="type-chip" style={{ marginLeft: 4, fontSize: '0.65rem', background: 'var(--color-text-secondary)', color: '#fff' }}>audit</span>}
                              </td>
                              <td>
                                <span className="type-chip">{field.type}</span>
                                {field.uiType && <span className="text-muted" style={{ fontSize: '0.72rem', display: 'block' }}>{field.uiType}</span>}
                              </td>
                              <td>{field.kind || 'editable'}</td>
                              <td style={{ fontSize: '0.8rem' }}>
                                {field.required && <span style={{ color: 'var(--color-danger)' }}>required </span>}
                                {field.allowMultiValues && <span style={{ color: 'var(--color-info)' }}>multi-value </span>}
                                {field.isLookup && <span style={{ color: 'var(--color-warning)' }}>lookup </span>}
                                {field.keywordChoices && (
                                  <span className="text-muted" style={{ display: 'block', fontSize: '0.72rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={field.keywordChoices}>
                                    choices: {field.keywordChoices}
                                  </span>
                                )}
                                {!field.required && !field.allowMultiValues && !field.isLookup && !field.keywordChoices && <span className="text-muted">—</span>}
                              </td>
                              <td>
                                {field.formulas && field.formulas.length > 0 ? (
                                  <div className="formula-list">
                                    {field.formulas.map((f, i) => (
                                      <code key={i} className="formula-code">{safeStr(f)}</code>
                                    ))}
                                  </div>
                                ) : field.defaultValue ? (
                                  <code className="formula-code">{safeStr(field.defaultValue)}</code>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Code events per field */}
                  {form.fields && form.fields.some(f => f.codeEvents && Object.keys(f.codeEvents).length > 0) && (
                    <div className="sub-section">
                      <h4>Field Code Events</h4>
                      {form.fields.filter(f => f.codeEvents && Object.keys(f.codeEvents).length > 0).map((field, fi) => (
                        <div key={fi} className="code-event-block">
                          <span className="code-event-field">{field.name}</span>
                          {Object.entries(field.codeEvents).map(([event, code]) => (
                            <div key={event} className="code-event-item">
                              <span className="code-event-label">{event}:</span>
                              <pre className="code-block">{safeStr(code, '')}</pre>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  {form.actions && form.actions.length > 0 && (
                    <div className="sub-section">
                      <h4>Actions ({form.actions.length})</h4>
                      {form.actions.map((action, ai) => (
                        <div key={ai} className="action-block">
                          <div className="action-header">
                            <strong>{action.label || action.name}</strong>
                            <span className="type-chip">{action.codeType || 'formula'}</span>
                          </div>
                          {action.code && <pre className="code-block">{safeStr(action.code, '')}</pre>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Code blocks (for subforms / misc) */}
                  {form.codeBlocks && form.codeBlocks.length > 0 && (
                    <div className="sub-section">
                      <h4>Code Blocks ({form.codeBlocks.length})</h4>
                      {form.codeBlocks.map((block, bi) => (
                        <div key={bi} className="code-event-item">
                          <span className="code-event-label">{safeStr(block.event || block.blockName)} ({safeStr(block.language, 'unknown')})</span>
                          <pre className="code-block">{safeStr(block.content, '')}</pre>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Collapsible action block showing action name, type, library imports, and code
 */
function ActionBlock({ action }) {
  const [expanded, setExpanded] = useState(false);
  const hasCode = action.code && action.code.trim().length > 0;
  const libs = action.libraries || [];
  const refs = action.references || {};
  const formRefs = refs.forms || [];
  const viewRefs = refs.views || [];
  const libRefs = refs.libraries || [];
  const hasRefs = formRefs.length > 0 || viewRefs.length > 0 || libRefs.length > 0;

  return (
    <div className="action-block" style={{ marginBottom: '0.5rem' }}>
      <div className="action-header" onClick={() => hasCode && setExpanded(!expanded)} style={{ cursor: hasCode ? 'pointer' : 'default' }}>
        <strong>{safeStr(action.label || action.name)}</strong>
        <span className="type-chip">{safeStr(action.codeType, 'formula')}</span>
        {libs.length > 0 && (
          <span className="meta-badge" style={{ fontSize: '0.75rem', color: 'var(--color-info)' }}>
            Uses: {libs.join(', ')}
          </span>
        )}
        {formRefs.length > 0 && (
          <span className="meta-badge" style={{ fontSize: '0.75rem', color: '#7c3aed' }}>
            Forms: {formRefs.join(', ')}
          </span>
        )}
        {hasCode && <span className="expand-arrow" style={{ marginLeft: 'auto', fontSize: '0.7rem' }}>{expanded ? '\u25B2' : '\u25BC'}</span>}
      </div>
      {expanded && hasCode && (
        <pre className="code-block" style={{ maxHeight: '400px', overflow: 'auto' }}>{safeStr(action.code, '')}</pre>
      )}
      {!hasCode && libs.length === 0 && !hasRefs && (
        <p style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)', margin: '0.2rem 0 0 0', fontStyle: 'italic' }}>No code (empty action or declarations only)</p>
      )}
    </div>
  );
}

/**
 * View code event block with meaningful label and description
 */
function ViewEventBlock({ evt }) {
  const [expanded, setExpanded] = useState(false);
  const codeLength = (evt.code || '').length;
  const isShort = codeLength < 200;

  return (
    <div className="code-event-item" style={{ marginBottom: '0.5rem' }}>
      <div onClick={() => !isShort && setExpanded(!expanded)} style={{ cursor: isShort ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span className="code-event-label" style={{ fontWeight: 600 }}>{safeStr(evt.label || evt.event)}</span>
        <span className="type-chip" style={{ fontSize: '0.72rem' }}>{safeStr(evt.language, 'formula')}</span>
        {evt.description && <span style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{evt.description}</span>}
        {!isShort && <span className="expand-arrow" style={{ marginLeft: 'auto', fontSize: '0.7rem' }}>{expanded ? '\u25B2' : '\u25BC'}</span>}
      </div>
      {(isShort || expanded) && (
        <pre className="code-block" style={{ maxHeight: expanded ? '500px' : '150px', overflow: 'auto', marginTop: '0.25rem' }}>{safeStr(evt.code, '')}</pre>
      )}
    </div>
  );
}

function ViewsTab({ views }) {
  const [expandedView, setExpandedView] = useState(null);
  if (!views || !views.views || views.views.length === 0) return <p>No views found in this application.</p>;

  return (
    <div className="analysis-tab-content">
      <p className="tab-summary">{views.totalViews} views ({views.categorizedCount || 0} categorized, {views.calendarCount || 0} calendar)</p>
      <div className="element-list">
        {views.views.map((view, idx) => {
          const isExpanded = expandedView === idx;
          const systemActions = (view.actions || []).filter(a => a.codeType === 'system');
          const customActions = (view.actions || []).filter(a => a.codeType !== 'system');
          return (
            <div key={idx} className={`element-card ${isExpanded ? 'expanded' : ''}`}>
              <div className="element-card-header" onClick={() => setExpandedView(isExpanded ? null : idx)}>
                <div className="element-card-title">
                  <span className="element-card-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-info)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
                  </span>
                  <strong>{view.viewName}</strong>
                  {view.viewAlias && <span className="element-alias">({view.viewAlias})</span>}
                </div>
                <div className="element-card-meta">
                  <span className="meta-badge">{view.columnCount || 0} columns</span>
                  <span className="type-chip">{view.viewType || 'standard'}</span>
                  {(view.actions || []).length > 0 && <span className="meta-badge">{(view.actions || []).length} actions</span>}
                  {view.hasCodeEvents && <span className="meta-badge" style={{ color: 'var(--color-warning)' }}>has code</span>}
                  {(view.referencedForms || []).length > 0 && <span className="meta-badge" style={{ color: 'var(--color-info)' }}>{view.referencedForms.length} form refs</span>}
                  <span className="expand-arrow">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                </div>
              </div>
              {isExpanded && (
                <div className="element-card-body">
                  {/* Selection formula */}
                  {view.selectionFormula && (
                    <div className="sub-section">
                      <h4>Selection Formula</h4>
                      <pre className="code-block">{view.selectionFormula}</pre>
                    </div>
                  )}

                  {/* Referenced forms */}
                  {view.referencedForms && view.referencedForms.length > 0 && (
                    <div className="sub-section">
                      <h4>Referenced Forms</h4>
                      <div className="feature-tags">
                        {view.referencedForms.map((ref, ri) => (
                          <span key={ri} className="feature-tag" style={{ background: ref.verified ? 'var(--color-info-bg, #e0f2fe)' : 'var(--color-warning-bg, #fef3c7)', color: ref.verified ? 'var(--color-info)' : 'var(--color-warning)', cursor: 'default' }}>
                            {ref.name} {ref.verified ? '✓' : '?'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Referenced libraries */}
                  {view.referencedLibraries && view.referencedLibraries.length > 0 && (
                    <div className="sub-section">
                      <h4>Referenced Libraries</h4>
                      <div className="feature-tags">
                        {view.referencedLibraries.map((ref, ri) => (
                          <span key={ri} className="feature-tag" style={{ background: ref.verified ? '#e0f2fe' : '#fef3c7', color: ref.verified ? 'var(--color-info)' : 'var(--color-warning)', cursor: 'default' }}>
                            {ref.name} {ref.verified ? '✓' : '?'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Columns table */}
                  {view.columns && view.columns.length > 0 && (
                    <div className="sub-section">
                      <h4>Columns ({view.columns.length})</h4>
                      <table className="mini-table">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Item Name</th>
                            <th>Sorted</th>
                            <th>Categorized</th>
                            <th>Hidden</th>
                            <th>Formula</th>
                          </tr>
                        </thead>
                        <tbody>
                          {view.columns.map((col, ci) => (
                            <tr key={ci}>
                              <td><strong>{col.title || '(untitled)'}</strong></td>
                              <td><code>{col.itemName || '—'}</code></td>
                              <td>{col.isSorted ? (col.sortDirection || 'Yes') : 'No'}</td>
                              <td>{col.isCategorized ? 'Yes' : 'No'}</td>
                              <td>{col.isHidden ? 'Yes' : 'No'}</td>
                              <td>{col.formula ? <code className="formula-code">{col.formula}</code> : <span className="text-muted">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Custom actions with actual code */}
                  {customActions.length > 0 && (
                    <div className="sub-section">
                      <h4>Custom Actions ({customActions.length})</h4>
                      {customActions.map((action, ai) => (
                        <ActionBlock key={ai} action={action} />
                      ))}
                    </div>
                  )}

                  {/* View code events (queryopen, queryopendocument, etc.) */}
                  {view.codeEvents && view.codeEvents.length > 0 && (
                    <div className="sub-section">
                      <h4>View Events ({view.codeEvents.length})</h4>
                      {view.codeEvents.map((evt, ei) => (
                        <ViewEventBlock key={ei} evt={evt} />
                      ))}
                    </div>
                  )}

                  {/* System commands (built-in Domino actions) */}
                  {systemActions.length > 0 && (
                    <div className="sub-section">
                      <h4>System Actions ({systemActions.length})</h4>
                      <div className="feature-tags">
                        {systemActions.map((action, ai) => (
                          <span key={ai} className="feature-tag" style={{ background: '#f3f4f6', color: '#374151', cursor: 'default' }}>
                            {safeStr(action.systemCommandLabel || action.label || action.name)}
                          </span>
                        ))}
                      </div>
                      <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '0.4rem' }}>
                        These are built-in Domino commands — no custom code to migrate.
                      </p>
                    </div>
                  )}

                  {/* Migration recommendation */}
                  {view.migrationRecommendation && typeof view.migrationRecommendation === 'object' && (
                    <div className="sub-section">
                      <h4>Migration Recommendation</h4>
                      <div className="detail-kv-list">
                        {view.migrationRecommendation.priority && <KV label="Priority" value={view.migrationRecommendation.priority} />}
                        {view.migrationRecommendation.estimatedEffort && <KV label="Effort" value={view.migrationRecommendation.estimatedEffort} />}
                        {view.migrationRecommendation.viewType && <KV label="View Type" value={view.migrationRecommendation.viewType} />}
                      </div>
                      {view.migrationRecommendation.recommendations && view.migrationRecommendation.recommendations.length > 0 && (
                        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                          {view.migrationRecommendation.recommendations.map((rec, ri) => (
                            <li key={ri}>{typeof rec === 'string' ? rec : JSON.stringify(rec)}</li>
                          ))}
                        </ul>
                      )}
                      {view.migrationRecommendation.issues && view.migrationRecommendation.issues.length > 0 && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong style={{ fontSize: '0.82rem' }}>Issues:</strong>
                          <ul style={{ paddingLeft: '1.25rem', margin: '0.25rem 0', fontSize: '0.85rem', color: 'var(--color-warning)' }}>
                            {view.migrationRecommendation.issues.map((issue, ii) => (
                              <li key={ii}>{typeof issue === 'string' ? issue : JSON.stringify(issue)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgentsTab({ results }) {
  const [expandedAgent, setExpandedAgent] = useState(null);
  const agentData = (results && results.agents) || {};
  const rawAgents = agentData.agents || [];
  const scriptData = (results && results.scriptLibraries) || {};
  const scriptLibs = scriptData.libraries || [];

  if (rawAgents.length === 0 && scriptLibs.length === 0) {
    return <div className="analysis-tab-content"><p>No agents or script libraries found.</p></div>;
  }

  return (
    <div className="analysis-tab-content">
      <p className="tab-summary">{rawAgents.length} agents, {scriptLibs.length} script libraries</p>
      <div className="element-list">
        {rawAgents.map((agent, idx) => {
          const isExpanded = expandedAgent === idx;
          return (
            <div key={idx} className={`element-card ${isExpanded ? 'expanded' : ''}`}>
              <div className="element-card-header" onClick={() => setExpandedAgent(isExpanded ? null : idx)}>
                <div className="element-card-title">
                  <span className="element-card-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/></svg>
                  </span>
                  <strong>{agent.name}</strong>
                </div>
                <div className="element-card-meta">
                  {agent.trigger && <span className="type-chip">{safeStr(agent.trigger.trigger, 'manual')}</span>}
                  {agent.trigger && agent.trigger.documentSet && <span className="meta-badge">{safeStr(agent.trigger.documentSet)}</span>}
                  <span className="meta-badge">{(agent.codeBlocks || []).length + (agent.actions || []).length} blocks</span>
                  <span className="expand-arrow">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                </div>
              </div>
              {isExpanded && (
                <div className="element-card-body">
                  {agent.trigger && (
                    <div className="sub-section">
                      <h4>Trigger</h4>
                      <div className="detail-kv-list">
                        <KV label="Type" value={safeStr(agent.trigger.trigger, 'manual')} />
                        {agent.trigger.documentSet && <KV label="Document Set" value={safeStr(agent.trigger.documentSet)} />}
                        {agent.trigger.restrict && <KV label="Restrict" value={safeStr(agent.trigger.restrict)} />}
                      </div>
                    </div>
                  )}
                  {/* Metadata (dates, author) */}
                  {agent.metadata && (agent.metadata.created || agent.metadata.modified || (agent.metadata.updatedBy && agent.metadata.updatedBy.length > 0)) && (
                    <div className="sub-section">
                      <h4>Metadata</h4>
                      <div className="detail-kv-list">
                        {agent.metadata.created && <KV label="Created" value={safeStr(agent.metadata.created)} />}
                        {agent.metadata.modified && <KV label="Modified" value={safeStr(agent.metadata.modified)} />}
                        {agent.metadata.updatedBy && agent.metadata.updatedBy.length > 0 && <KV label="Updated By" value={agent.metadata.updatedBy.map(n => safeStr(n)).join(', ')} />}
                        {agent.metadata.signedBy && agent.metadata.signedBy.length > 0 && <KV label="Signed By" value={agent.metadata.signedBy.map(n => safeStr(n)).join(', ')} />}
                      </div>
                    </div>
                  )}
                  {/* Simple actions (modifyfield, runformula, etc.) */}
                  {agent.actions && agent.actions.length > 0 && (
                    <div className="sub-section">
                      <h4>Simple Actions ({agent.actions.length})</h4>
                      {agent.actions.map((action, ai) => (
                        <div key={ai} className="action-block">
                          <div className="action-header">
                            <strong>{safeStr(action.type)}</strong>
                            {action.field && <span className="type-chip">{safeStr(action.field)}{action.value ? ` = "${safeStr(action.value)}"` : ''}</span>}
                          </div>
                          {action.formula && <pre className="code-block">{safeStr(action.formula, '')}</pre>}
                        </div>
                      ))}
                    </div>
                  )}
                  {agent.codeBlocks && agent.codeBlocks.length > 0 && (
                    <div className="sub-section">
                      <h4>Code ({agent.codeBlocks.length} blocks)</h4>
                      {agent.codeBlocks.map((block, bi) => (
                        <div key={bi} className="code-event-item">
                          <span className="code-event-label">{safeStr(block.event || block.blockName)} — {safeStr(block.language, 'unknown')}</span>
                          <pre className="code-block">{safeStr(block.content, '')}</pre>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Script Libraries */}
      {scriptLibs.length > 0 && (
        <>
          <h3 style={{ marginTop: '1.5rem' }}>Script Libraries ({scriptLibs.length})</h3>
          <div className="element-list">
            {scriptLibs.map((lib, idx) => (
              <div key={`sl-${idx}`} className="element-card">
                <div className="element-card-header" onClick={() => setExpandedAgent(expandedAgent === `sl-${idx}` ? null : `sl-${idx}`)}>
                  <div className="element-card-title">
                    <span className="element-card-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-info)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                    </span>
                    <strong>{lib.name}</strong>
                    {lib.alias && <span className="element-alias">({lib.alias})</span>}
                  </div>
                  <div className="element-card-meta">
                    <span className="meta-badge">{(lib.codeBlocks || []).length} code blocks</span>
                    <span className="expand-arrow">{expandedAgent === `sl-${idx}` ? '\u25B2' : '\u25BC'}</span>
                  </div>
                </div>
                {expandedAgent === `sl-${idx}` && lib.codeBlocks && lib.codeBlocks.length > 0 && (
                  <div className="element-card-body">
                    {lib.codeBlocks.map((block, bi) => (
                      <div key={bi} className="code-event-item">
                        <span className="code-event-label">{safeStr(block.event || block.blockName)} — {safeStr(block.language, 'unknown')}</span>
                        <pre className="code-block">{safeStr(block.content, '')}</pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RelationshipsTab({ relationships }) {
  const [expandedLookup, setExpandedLookup] = useState(null);
  const [expandedOverlap, setExpandedOverlap] = useState(null);
  const [expandedDep, setExpandedDep] = useState(null);
  if (!relationships) return <p>No relationship data available.</p>;
  const lookups = relationships.lookupRelationships || [];
  const overlaps = relationships.fieldOverlapRelationships || [];
  const computedDeps = relationships.computedDependencies || [];
  const depGraph = relationships.dependencyGraph || {};
  const summary = relationships.summary || {};

  return (
    <div className="analysis-tab-content">
      <p className="tab-summary">
        {lookups.length + overlaps.length} relationships detected ({lookups.length} lookups, {overlaps.length} shared fields
        {computedDeps.length > 0 ? `, ${computedDeps.length} computed dependencies` : ''})
      </p>

      {/* Summary stats */}
      {Object.keys(summary).length > 0 && (
        <div className="analysis-overview-grid" style={{ marginBottom: '1.5rem' }}>
          {summary.totalLookups !== undefined && <StatCard label="Lookups" value={summary.totalLookups} />}
          {summary.totalFieldOverlaps !== undefined && <StatCard label="Shared Fields" value={summary.totalFieldOverlaps} />}
          {summary.totalComputedDependencies !== undefined && <StatCard label="Computed Deps" value={summary.totalComputedDependencies} />}
          {summary.uniqueFormPairs !== undefined && <StatCard label="Form Pairs" value={summary.uniqueFormPairs} />}
        </div>
      )}

      {lookups.length > 0 && (
        <div className="analysis-section">
          <h3>Lookup Relationships ({lookups.length})</h3>
          <div className="element-list">
            {lookups.map((rel, i) => (
              <div key={i} className={`element-card ${expandedLookup === i ? 'expanded' : ''}`}>
                <div className="element-card-header" onClick={() => setExpandedLookup(expandedLookup === i ? null : i)}>
                  <div className="element-card-title">
                    <strong>{rel.sourceForm}</strong>
                    <span style={{ margin: '0 0.4rem', color: 'var(--color-text-secondary)' }}>{'\u2192'}</span>
                    <strong style={{ color: 'var(--color-info)' }}>{rel.targetForm || '—'}</strong>
                  </div>
                  <div className="element-card-meta">
                    <span className="type-chip">{rel.type || 'lookup'}</span>
                    <span className="meta-badge" style={{ color: rel.confidence >= 0.8 ? 'var(--color-success)' : 'var(--color-warning)' }}>
                      {Math.round((rel.confidence || 0) * 100)}%
                    </span>
                    <span className="expand-arrow">{expandedLookup === i ? '\u25B2' : '\u25BC'}</span>
                  </div>
                </div>
                {expandedLookup === i && (
                  <div className="element-card-body">
                    <div className="detail-kv-list">
                      {rel.fieldName && <KV label="Source Field" value={rel.fieldName} />}
                      {rel.lookupField && <KV label="Lookup Column" value={rel.lookupField} />}
                      {rel.targetType && <KV label="Target Type" value={rel.targetType} />}
                      <KV label="Confidence" value={`${Math.round((rel.confidence || 0) * 100)}%`} />
                    </div>
                    {rel.evidence && (
                      <p style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>{rel.evidence}</p>
                    )}
                    {rel.formulaSnippet && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <pre className="code-block" style={{ fontSize: '0.78rem' }}>{rel.formulaSnippet}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {overlaps.length > 0 && (
        <div className="analysis-section">
          <h3>Shared Field Relationships ({overlaps.length})</h3>
          <p style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>
            Fields that appear across multiple forms — potential foreign keys, shared identifiers, or data duplication candidates.
          </p>
          <div className="element-list">
            {overlaps.map((rel, i) => {
              const isExpanded = expandedOverlap === i;
              return (
                <div key={i} className={`element-card ${isExpanded ? 'expanded' : ''}`}>
                  <div className="element-card-header" onClick={() => setExpandedOverlap(isExpanded ? null : i)}>
                    <div className="element-card-title">
                      <code style={{ fontWeight: 600, fontSize: '0.9rem' }}>{rel.fieldName}</code>
                      <span style={{ margin: '0 0.5rem', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                        in {rel.formCount || (rel.forms || []).length} forms
                      </span>
                    </div>
                    <div className="element-card-meta">
                      <span className="type-chip">{rel.relationshipType === 'foreign_key_candidate' ? 'FK candidate' : rel.relationshipType === 'shared_identifier' ? 'shared ID' : 'shared field'}</span>
                      <span className="meta-badge" style={{ color: rel.confidence >= 0.7 ? 'var(--color-success)' : 'var(--color-warning)' }}>
                        {Math.round((rel.confidence || 0) * 100)}%
                      </span>
                      <span className="expand-arrow">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="element-card-body">
                      {rel.meaning && (
                        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>{rel.meaning}</p>
                      )}
                      <div className="detail-kv-list">
                        <KV label="Forms" value={(rel.forms || []).join(', ')} />
                        <KV label="Field Types" value={(rel.fieldTypes || []).join(', ')} />
                        <KV label="Types Consistent" value={rel.typesConsistent ? 'Yes' : 'No — needs reconciliation'} />
                      </div>
                      {rel.formPairs && rel.formPairs.length > 0 && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong style={{ fontSize: '0.82rem' }}>Form Pairs:</strong>
                          <table className="mini-table" style={{ marginTop: '0.25rem' }}>
                            <thead><tr><th>Form 1</th><th>Form 2</th><th>Type 1</th><th>Type 2</th></tr></thead>
                            <tbody>
                              {rel.formPairs.map((pair, pi) => (
                                <tr key={pi}>
                                  <td><strong>{pair.form1}</strong></td>
                                  <td><strong>{pair.form2}</strong></td>
                                  <td><code>{pair.type1 || '—'}</code></td>
                                  <td><code>{pair.type2 || '—'}</code></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {computedDeps && computedDeps.length > 0 && (
        <div className="analysis-section">
          <h3>Computed Dependencies ({computedDeps.length})</h3>
          <p style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>
            Computed and computed-for-display fields with their formulas, dependency classification, and migration approach.
          </p>
          <div className="element-list">
            {computedDeps.map((dep, i) => {
              const isExpanded = expandedDep === i;
              return (
                <div key={i} className={`element-card ${isExpanded ? 'expanded' : ''}`}>
                  <div className="element-card-header" onClick={() => setExpandedDep(isExpanded ? null : i)}>
                    <div className="element-card-title">
                      <strong>{dep.sourceForm}</strong>
                      <span style={{ margin: '0 0.3rem', color: 'var(--color-text-secondary)' }}>.</span>
                      <code>{dep.computedField}</code>
                    </div>
                    <div className="element-card-meta">
                      <span className="type-chip">{dep.dependencyLabel || dep.dependencyType || 'computed'}</span>
                      <span className="meta-badge">{dep.fieldKind === 'computedfordisplay' ? 'display' : 'stored'}</span>
                      <span className="expand-arrow">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="element-card-body">
                      <div className="detail-kv-list">
                        <KV label="Kind" value={dep.fieldKind || '—'} />
                        <KV label="Classification" value={dep.dependencyLabel || dep.dependencyType || '—'} />
                        <KV label="Depends On" value={Array.isArray(dep.dependsOn) && dep.dependsOn.length > 0 ? dep.dependsOn.join(', ') : 'No field references detected'} />
                        {dep.migrationApproach && <KV label="Migration Approach" value={dep.migrationApproach} />}
                      </div>
                      {dep.formula && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong style={{ fontSize: '0.82rem' }}>Formula:</strong>
                          <pre className="code-block" style={{ fontSize: '0.78rem', marginTop: '0.25rem' }}>{dep.formula}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dependency graph */}
      {Object.keys(depGraph).length > 0 && (
        <div className="analysis-section">
          <h3>Dependency Graph</h3>
          <p className="tab-summary">Forms and their outgoing/incoming relationships</p>
          <div className="element-list">
            {Object.entries(depGraph).map(([form, info]) => {
              const deps = Array.isArray(info) ? info : (info.dependencies || []);
              const dependents = info.dependents || [];
              if (deps.length === 0 && dependents.length === 0) return null;
              return (
                <div key={form} className="element-card">
                  <div className="element-card-header" style={{ cursor: 'default' }}>
                    <div className="element-card-title">
                      <strong>{form}</strong>
                    </div>
                    <div className="element-card-meta">
                      {deps.length > 0 && <span className="meta-badge">{deps.length} outgoing</span>}
                      {dependents.length > 0 && <span className="meta-badge">{dependents.length} incoming</span>}
                    </div>
                  </div>
                  <div className="element-card-body" style={{ paddingTop: '0.4rem' }}>
                    {deps.length > 0 && (
                      <p style={{ fontSize: '0.83rem' }}>
                        <strong>Depends on:</strong>{' '}
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                          {deps.map(t => typeof t === 'string' ? t : (t.target || t.form || JSON.stringify(t))).join(', ')}
                        </span>
                      </p>
                    )}
                    {dependents.length > 0 && (
                      <p style={{ fontSize: '0.83rem', marginTop: '0.2rem' }}>
                        <strong>Used by:</strong>{' '}
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                          {dependents.map(t => typeof t === 'string' ? t : (t.target || t.form || JSON.stringify(t))).join(', ')}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function WorkflowsTab({ workflows, validation, consolidation, junction, domain }) {
  const wfList = (workflows && workflows.workflows) || [];
  const valRules = (validation && validation.validationRules) || [];
  const requiredFields = (validation && validation.requiredFields) || [];
  const computedFields = (validation && validation.computedFieldAnalysis) || [];
  const bizRules = (validation && validation.businessRules) || [];
  const consol = consolidation || {};
  const consolOpps = consol.consolidationOpportunities || [];
  const dupes = consol.duplicateForms || [];
  const junc = junction || {};
  const m2m = junc.manyToManyCandidates || [];
  const juncCandidates = junc.junctionTableCandidates || [];
  const domainMappings = (domain && domain.entityMappings) || [];
  const domainScores = (domain && domain.domainScores) || {};

  return (
    <div className="analysis-tab-content">
      {/* ─── Business Domain ─── */}
      <div className="analysis-section">
        <h3>Business Domain</h3>
        {domain && domain.detectedDomain && domain.detectedDomain !== 'Unknown' ? (
          <div>
            <div className="analysis-overview-grid" style={{ marginBottom: '1rem' }}>
              <StatCard label="Detected Domain" value={safeStr(domain.detectedDomain)} />
              <StatCard label="Confidence" value={`${Math.round((domain.domainConfidence || 0) * 100)}%`} />
              <StatCard label="Matching Forms" value={domainMappings.filter(m => m.domain === domain.detectedDomain).length} />
            </div>
            {domainMappings.length > 0 && (
              <div>
                <h4>Form–Domain Mapping</h4>
                <table className="mini-table">
                  <thead><tr><th>Form</th><th>Domain</th><th>Confidence</th><th>Matched Keywords</th></tr></thead>
                  <tbody>
                    {domainMappings.map((em, i) => (
                      <tr key={i}>
                        <td><strong>{safeStr(em.displayName || em.entityName)}</strong></td>
                        <td><span className="type-chip">{safeStr(em.domain)}</span></td>
                        <td>{Math.round((em.confidence || 0) * 100)}%</td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)' }}>
                          {(em.matchedKeywords || []).slice(0, 4).join(', ')}
                          {(em.matchedKeywords || []).length > 4 ? '...' : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {Object.keys(domainScores).length > 1 && (
              <div style={{ marginTop: '0.75rem' }}>
                <h4>Other Possible Domains</h4>
                <div className="feature-tags">
                  {Object.entries(domainScores)
                    .filter(([d]) => d !== domain.detectedDomain)
                    .map(([d, info], i) => (
                      <span key={i} className="feature-tag">{d} ({info.matchingForms} form{info.matchingForms !== 1 ? 's' : ''})</span>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-muted">No specific business domain detected. The application appears to be general-purpose or custom.</p>
        )}
      </div>

      {/* ─── Workflows ─── */}
      <div className="analysis-section">
        <h3>Workflows ({wfList.length})</h3>
        {wfList.length === 0 ? <p className="text-muted">No workflow patterns detected in this application.</p> : (
          <div className="element-list">
            {wfList.map((wf, i) => (
              <div key={i} className="element-card">
                <div className="element-card-body" style={{ padding: '0.75rem 1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <strong style={{ fontSize: '1rem' }}>{wf.elementName || 'Unknown'}</strong>
                    <span className="type-chip" style={{ background: 'var(--color-info)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>
                      {safeStr(wf.workflowType) || wf.elementType || '—'}
                    </span>
                  </div>
                  {/* Description — the key client-facing summary */}
                  {wf.description && (
                    <p style={{ fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '0.5rem', color: 'var(--color-text-primary)' }}>
                      {safeStr(wf.description)}
                    </p>
                  )}
                  {/* State machine */}
                  {wf.stateMachine && wf.stateMachine.knownStates && wf.stateMachine.knownStates.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>States: </span>
                      <span style={{ fontSize: '0.82rem' }}>
                        {wf.stateMachine.knownStates.map((s, si) => (
                          <span key={si}>
                            {si > 0 && <span style={{ color: 'var(--color-text-secondary)', margin: '0 4px' }}> → </span>}
                            <code style={{ background: 'var(--color-bg-tertiary)', padding: '1px 5px', borderRadius: '3px' }}>{safeStr(s)}</code>
                          </span>
                        ))}
                      </span>
                    </div>
                  )}
                  {/* Actions & Notifications badges */}
                  <div className="feature-tags" style={{ marginTop: '0.35rem' }}>
                    {wf.hasApproval && <span className="feature-tag" style={{ background: '#fef3c7', color: '#92400e' }}>Approval</span>}
                    {wf.hasRouting && <span className="feature-tag" style={{ background: '#dbeafe', color: '#1e40af' }}>Routing</span>}
                    {wf.hasNotifications && <span className="feature-tag" style={{ background: '#d1fae5', color: '#065f46' }}>Email Notifications</span>}
                    {wf.actions && wf.actions.length > 0 && (
                      <span className="feature-tag">{wf.actions.length} action{wf.actions.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  {/* Migration guidance */}
                  {wf.migrationGuidance && wf.migrationGuidance.length > 0 && (
                    <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'var(--color-bg-secondary)', borderRadius: '6px', borderLeft: '3px solid var(--color-info)' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-secondary)' }}>Migration Guidance</span>
                      {wf.migrationGuidance.map((g, gi) => (
                        <div key={gi} style={{ fontSize: '0.82rem', marginTop: '0.25rem' }}>
                          <span style={{ fontWeight: 500 }}>{safeStr(g.category)}: </span>
                          <span>{safeStr(g.recommendation)}</span>
                          {g.effort && <span className="meta-badge" style={{ marginLeft: '6px', fontSize: '0.7rem' }}>{safeStr(g.effort)} effort</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Validation & Business Rules ─── */}
      {(valRules.length > 0 || requiredFields.length > 0 || bizRules.length > 0) && (
        <div className="analysis-section">
          <h3>Validation & Business Rules</h3>
          <div className="analysis-overview-grid" style={{ marginBottom: '1rem' }}>
            <StatCard label="Validation Rules" value={valRules.length} />
            <StatCard label="Required Fields" value={requiredFields.length} />
            <StatCard label="Computed Fields" value={computedFields.length} />
            <StatCard label="Data Constraints" value={bizRules.length} />
          </div>

          {valRules.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h4>Validation Rules ({valRules.length})</h4>
              <table className="mini-table">
                <thead><tr><th>Form</th><th>Field</th><th>Type</th><th>Description</th><th>Migration</th></tr></thead>
                <tbody>
                  {valRules.map((rule, i) => (
                    <tr key={i}>
                      <td><strong>{safeStr(rule.form)}</strong></td>
                      <td><code>{safeStr(rule.field)}</code></td>
                      <td><span className="type-chip">{safeStr(rule.ruleType)}</span></td>
                      <td style={{ fontSize: '0.82rem' }}>{safeStr(rule.ruleDescription)}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{safeStr(rule.migrationNote)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {requiredFields.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h4>Required Fields ({requiredFields.length})</h4>
              <table className="mini-table">
                <thead><tr><th>Form</th><th>Field</th><th>Type</th><th>Why Required</th></tr></thead>
                <tbody>
                  {requiredFields.map((rf, i) => (
                    <tr key={i}>
                      <td><strong>{safeStr(rf.form)}</strong></td>
                      <td><code>{safeStr(rf.field)}</code></td>
                      <td><span className="type-chip">{safeStr(rf.fieldType)}</span></td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {Array.isArray(rf.reasons) ? rf.reasons.join('; ') : safeStr(rf.reasons || rf.reason)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {bizRules.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h4>Data Constraints ({bizRules.length})</h4>
              <table className="mini-table">
                <thead><tr><th>Form</th><th>Field</th><th>Constraint</th><th>Migration</th></tr></thead>
                <tbody>
                  {bizRules.map((br, i) => (
                    <tr key={i}>
                      <td><strong>{safeStr(br.form)}</strong></td>
                      <td><code>{safeStr(br.field)}</code></td>
                      <td style={{ fontSize: '0.82rem' }}>{safeStr(br.ruleDescription)}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{safeStr(br.migrationNote)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {computedFields.length > 0 && (
            <div>
              <h4>Computed Fields ({computedFields.length})</h4>
              <table className="mini-table">
                <thead><tr><th>Form</th><th>Field</th><th>Logic Type</th><th>Description</th><th>Migration</th></tr></thead>
                <tbody>
                  {computedFields.map((cf, i) => (
                    <tr key={i}>
                      <td><strong>{safeStr(cf.form)}</strong></td>
                      <td><code>{safeStr(cf.field)}</code></td>
                      <td><span className="type-chip">{safeStr(cf.logicType)}</span></td>
                      <td style={{ fontSize: '0.82rem' }}>{safeStr(cf.description)}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{safeStr(cf.migrationNote)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── Form Consolidation ─── */}
      {(dupes.length > 0 || consolOpps.length > 0) && (
        <div className="analysis-section">
          <h3>Form Consolidation Opportunities</h3>
          {consol.summary && consol.summary.potentialSavings && (
            <div className="analysis-overview-grid" style={{ marginBottom: '1rem' }}>
              <StatCard label="Duplicate Pairs" value={consol.summary.duplicateForms || 0} />
              <StatCard label="Consolidation Candidates" value={consol.summary.consolidationOpportunities || 0} />
              <StatCard label="Est. Dev Time Saved" value={safeStr(consol.summary.potentialSavings.developmentTimeSaved)} />
              <StatCard label="Maint. Reduction" value={safeStr(consol.summary.potentialSavings.maintenanceReduction)} />
            </div>
          )}
          {dupes.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h4>Duplicate Forms (High Priority)</h4>
              <div className="element-list">
                {dupes.map((dup, i) => (
                  <div key={i} className="element-card" style={{ borderLeft: '3px solid var(--color-danger)' }}>
                    <div className="element-card-body" style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                        <span><strong>{dup.form1}</strong> ≈ <strong>{dup.form2}</strong></span>
                        <span className="meta-badge">{Math.round((dup.similarityScore || 0) * 100)}% match</span>
                      </div>
                      <p style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', margin: 0 }}>
                        {safeStr(dup.consolidationStrategy)}
                        {dup.commonFields && dup.commonFields.length > 0 &&
                          ` (${dup.commonFields.length} shared field${dup.commonFields.length !== 1 ? 's' : ''})`
                        }
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {consolOpps.length > 0 && (
            <div>
              <h4>Consolidation Candidates</h4>
              <div className="element-list">
                {consolOpps.map((opp, i) => (
                  <div key={i} className="element-card">
                    <div className="element-card-body" style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                        <span><strong>{opp.form1}</strong> + <strong>{opp.form2}</strong></span>
                        <span className="meta-badge">{Math.round((opp.similarityScore || 0) * 100)}% similar</span>
                      </div>
                      <p style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', margin: 0 }}>
                        {opp.commonFields && opp.commonFields.length > 0
                          ? `${opp.commonFields.length} common field${opp.commonFields.length !== 1 ? 's' : ''}`
                          : 'Field overlap detected'
                        }
                        {opp.uniqueFieldsForm1 && opp.uniqueFieldsForm1.length > 0 &&
                          ` · ${opp.form1} has ${opp.uniqueFieldsForm1.length} unique field${opp.uniqueFieldsForm1.length !== 1 ? 's' : ''}`
                        }
                        {opp.uniqueFieldsForm2 && opp.uniqueFieldsForm2.length > 0 &&
                          ` · ${opp.form2} has ${opp.uniqueFieldsForm2.length} unique field${opp.uniqueFieldsForm2.length !== 1 ? 's' : ''}`
                        }
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Relationship Analysis (M2M / Junction) ─── */}
      {(m2m.length > 0 || juncCandidates.length > 0) && (
        <div className="analysis-section">
          <h3>Relationship Analysis</h3>
          {m2m.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h4>Many-to-Many Candidates ({m2m.length})</h4>
              <p style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                These form pairs may need junction tables in the target database.
              </p>
              <table className="mini-table">
                <thead><tr><th>Form 1</th><th>Form 2</th><th>Confidence</th><th>Evidence</th></tr></thead>
                <tbody>
                  {m2m.map((rel, i) => {
                    const evidence = [];
                    if (rel.businessPatterns && rel.businessPatterns.length > 0) evidence.push('Business pattern match');
                    if (rel.lookupRelationships && rel.lookupRelationships.length > 0) evidence.push(`${rel.lookupRelationships.length} lookup(s)`);
                    if (rel.relatedFields && rel.relatedFields.length > 0) evidence.push(`${rel.relatedFields.length} related field(s)`);
                    return (
                      <tr key={i}>
                        <td><strong>{safeStr(rel.form1)}</strong></td>
                        <td><strong>{safeStr(rel.form2)}</strong></td>
                        <td>{Math.round((rel.confidence || 0) * 100)}%</td>
                        <td style={{ fontSize: '0.82rem' }}>{evidence.join(', ') || 'Field overlap'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {juncCandidates.length > 0 && (
            <div>
              <h4>Existing Junction Tables ({juncCandidates.length})</h4>
              <p style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                These forms appear to already function as junction/linking tables.
              </p>
              <table className="mini-table">
                <thead><tr><th>Form</th><th>Fields</th><th>ID Fields</th><th>FK Patterns</th></tr></thead>
                <tbody>
                  {juncCandidates.map((jc, i) => (
                    <tr key={i}>
                      <td><strong>{safeStr(jc.formName)}</strong></td>
                      <td>{jc.fieldCount || '—'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{(jc.idFields || []).slice(0, 3).join(', ') || '—'}</td>
                      <td style={{ fontSize: '0.82rem' }}>{(jc.foreignKeyPatterns || []).slice(0, 3).join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SecurityTab({ security }) {
  if (!security) return <p>No security data available.</p>;
  const entries = security.aclEntries || [];
  const roles = security.roles || [];
  const risks = security.securityRisks || [];

  return (
    <div className="analysis-tab-content">
      <p className="tab-summary">{entries.length} ACL entries, {roles.length} roles, {risks.length} security risks</p>

      {entries.length > 0 && (
        <div className="analysis-section">
          <h3>Access Control List ({entries.length})</h3>
          <table className="mini-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Level</th>
                <th>Type</th>
                <th>Create Docs</th>
                <th>Delete Docs</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => {
                const levelLabels = { '0': 'No Access', '1': 'Depositor', '2': 'Reader', '3': 'Author', '4': 'Editor', '5': 'Designer', '6': 'Manager' };
                return (
                  <tr key={i}>
                    <td><strong>{entry.name || '(unnamed)'}</strong></td>
                    <td><span className="type-chip">{levelLabels[entry.level] || entry.level}</span></td>
                    <td>{entry.type}</td>
                    <td>{entry.canCreateDocuments ? 'Yes' : 'No'}</td>
                    <td>{entry.canDeleteDocuments ? 'Yes' : 'No'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {roles.length > 0 && (
        <div className="analysis-section">
          <h3>Roles ({roles.length})</h3>
          <div className="feature-tags">
            {roles.map((role, i) => <span key={i} className="feature-tag">{role}</span>)}
          </div>
        </div>
      )}

      {risks.length > 0 && (
        <div className="analysis-section">
          <h3>Security Risks ({risks.length})</h3>
          {risks.map((risk, i) => (
            <div key={i} className="risk-item">
              <span className="risk-severity" style={{ color: risk.severity === 'high' ? 'var(--color-danger)' : risk.severity === 'medium' ? 'var(--color-warning)' : 'var(--color-text-secondary)' }}>
                {risk.severity || 'info'}
              </span>
              <span>{safeStr(risk.description || risk.risk, JSON.stringify(risk))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SQLTab({ sql }) {
  const [activeDialect, setActiveDialect] = useState('postgresql');
  const [expandedTable, setExpandedTable] = useState(null);
  if (!sql) return <p>No SQL schema data available.</p>;
  const tables = sql.tables || [];
  const ddl = sql.ddlStatements || {};

  return (
    <div className="analysis-tab-content">
      <p className="tab-summary">{tables.length} tables generated</p>

      {/* Dialect selector */}
      {Object.keys(ddl).length > 0 && (
        <div className="analysis-section">
          <h3>DDL Statements</h3>
          <div className="dialect-tabs">
            {Object.keys(ddl).map(dialect => (
              <button key={dialect} className={`dialect-tab ${activeDialect === dialect ? 'active' : ''}`} onClick={() => setActiveDialect(dialect)}>
                {dialect}
              </button>
            ))}
          </div>
          <pre className="code-block sql-block">{ddl[activeDialect] || 'No DDL for this dialect.'}</pre>
        </div>
      )}

      {/* Table details */}
      {tables.length > 0 && (
        <div className="analysis-section">
          <h3>Table Definitions</h3>
          <div className="element-list">
            {tables.map((table, idx) => {
              const isExpanded = expandedTable === idx;
              return (
                <div key={idx} className={`element-card ${isExpanded ? 'expanded' : ''}`}>
                  <div className="element-card-header" onClick={() => setExpandedTable(isExpanded ? null : idx)}>
                    <div className="element-card-title">
                      <span className="element-card-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
                      </span>
                      <strong>{table.name}</strong>
                      {table.originalName && table.originalName !== table.name && (
                        <span className="element-alias">(from: {table.originalName})</span>
                      )}
                    </div>
                    <div className="element-card-meta">
                      <span className="meta-badge">{(table.columns || []).length} columns</span>
                      <span className="expand-arrow">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="element-card-body">
                      <table className="mini-table">
                        <thead>
                          <tr>
                            <th>Column</th>
                            <th>Type</th>
                            <th>Nullable</th>
                            <th>Source Field</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(table.columns || []).map((col, ci) => (
                            <tr key={ci}>
                              <td><strong>{col.name}</strong></td>
                              <td><code>{col.type || col.sqlType || '—'}</code></td>
                              <td>{col.nullable === false ? 'NOT NULL' : 'NULL'}</td>
                              <td>{col.sourceField || col.originalName || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {table.uniqueConstraints && table.uniqueConstraints.length > 0 && (
                        <div className="sub-section">
                          <h4>Unique Constraints</h4>
                          {table.uniqueConstraints.map((uc, i) => <code key={i} className="formula-code">{uc}</code>)}
                        </div>
                      )}
                      {table.checkConstraints && table.checkConstraints.length > 0 && (
                        <div className="sub-section">
                          <h4>Check Constraints</h4>
                          {table.checkConstraints.map((cc, i) => <code key={i} className="formula-code">{cc}</code>)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MigrationTab({ migration, complexity }) {
  if (!migration) return <p>No migration data available.</p>;
  const phases = migration.migrationPhases || [];
  const factors = migration.factors || [];
  const recommendations = migration.recommendations || [];
  const debt = migration.technicalDebtAssessment || {};
  const resource = migration.resourceEstimate || {};

  return (
    <div className="analysis-tab-content">
      {/* Top stats */}
      <div className="analysis-overview-grid" style={{ marginBottom: '1.5rem' }}>
        <StatCard label="Risk Level" value={migration.riskLevel || 'N/A'} color={complexityBadge(migration.riskLevel === 'high' ? 'High' : migration.riskLevel === 'medium' ? 'Medium' : 'Low')} />
        <StatCard label="Effort Level" value={migration.effortLevel || 'N/A'} />
        <StatCard label="Overall Score" value={migration.overallScore ? migration.overallScore.toFixed(1) : '0'} />
        <StatCard label="Phases" value={phases.length} />
        {resource.totalPersonDays && <StatCard label="Person-Days" value={resource.totalPersonDays} />}
        {resource.teamSize && <StatCard label="Team Size" value={resource.teamSize} />}
        {resource.parallelMonths && <StatCard label="Timeline" value={`${resource.parallelMonths}mo`} sub="(parallel)" />}
      </div>

      {/* Migration phases */}
      {phases.length > 0 && (
        <div className="analysis-section">
          <h3>Migration Phases ({phases.length})</h3>
          <div className="phases-timeline">
            {phases.map((phase, i) => (
              <div key={i} className="phase-card">
                <div className="phase-number">{phase.phase || i + 1}</div>
                <div className="phase-content">
                  <h4>{phase.name || `Phase ${i + 1}`}</h4>
                  {phase.description && <p>{phase.description}</p>}
                  {phase.estimatedDuration && <p><strong>Duration:</strong> {phase.estimatedDuration}</p>}
                  {phase.tasks && phase.tasks.length > 0 && (
                    <ul style={{ paddingLeft: '1.25rem', margin: '0.35rem 0', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                      {phase.tasks.map((t, ti) => <li key={ti}>{typeof t === 'string' ? t : JSON.stringify(t)}</li>)}
                    </ul>
                  )}
                  {phase.forms && phase.forms.length > 0 && (
                    <p style={{ fontSize: '0.85rem' }}><strong>Forms:</strong> {phase.forms.map(f => safeStr(f)).join(', ')}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resource estimate */}
      {resource.roles && resource.roles.length > 0 && (
        <div className="analysis-section">
          <h3>Recommended Team</h3>
          <table className="mini-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Count</th>
                <th>Focus Area</th>
              </tr>
            </thead>
            <tbody>
              {resource.roles.map((r, i) => (
                <tr key={i}>
                  <td><strong>{typeof r === 'string' ? r : r.role || 'Unknown'}</strong></td>
                  <td>{typeof r === 'object' ? r.count || 1 : 1}</td>
                  <td>{typeof r === 'object' ? r.focus || '' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {resource.assumptions && resource.assumptions.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <h4>Assumptions</h4>
              <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
                {resource.assumptions.map((a, i) => <li key={i} style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>{typeof a === 'string' ? a : JSON.stringify(a)}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Technical debt */}
      {debt.totalDebtScore > 0 && (
        <div className="analysis-section">
          <h3>Technical Debt Assessment (Score: {debt.totalDebtScore})</h3>
          {debt.debtCategories && debt.debtCategories.length > 0 && (
            <div className="element-list">
              {debt.debtCategories.map((cat, i) => (
                <div key={i} className="risk-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.35rem' }}>
                    <span className="risk-severity" style={{ color: cat.score >= 5 ? 'var(--color-danger)' : 'var(--color-warning)' }}>{cat.score} pts</span>
                    <strong style={{ textTransform: 'capitalize' }}>{(cat.category || '').replace(/_/g, ' ')}</strong>
                  </div>
                  {cat.issues && cat.issues.length > 0 && (
                    <ul style={{ paddingLeft: '1.25rem', margin: '0.25rem 0', fontSize: '0.82rem', color: 'var(--color-text-secondary)' }}>
                      {cat.issues.slice(0, 5).map((issue, j) => (
                        <li key={j}>{safeStr(issue.description || issue, JSON.stringify(issue))}</li>
                      ))}
                      {cat.issues.length > 5 && <li>...and {cat.issues.length - 5} more</li>}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
          {debt.modernizationOpportunities && debt.modernizationOpportunities.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h4>Modernization Opportunities</h4>
              {debt.modernizationOpportunities.map((opp, i) => (
                <div key={i} className="recommendation-item">
                  <span className="rec-priority">{opp.effort || 'medium'}</span>
                  <span><strong>{safeStr(opp.title)}:</strong> {safeStr(opp.description)}{opp.benefit ? ` — ${safeStr(opp.benefit)}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Risk factors */}
      {factors.length > 0 && (
        <div className="analysis-section">
          <h3>Risk Factors ({factors.length})</h3>
          {factors.map((f, i) => (
            <div key={i} className="risk-item">
              <span className="risk-severity" style={{ color: (f.riskWeight || 0) >= 2 ? 'var(--color-danger)' : 'var(--color-warning)' }}>
                {f.riskWeight ? `x${f.riskWeight}` : 'risk'}
              </span>
              <span><strong>{f.type ? safeStr(f.type).replace(/_/g, ' ') : 'Factor'}:</strong> {safeStr(f.description, '')}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="analysis-section">
          <h3>Recommendations</h3>
          {recommendations.map((rec, i) => (
            <div key={i} className="recommendation-item">
              <span className="rec-priority">{i + 1}</span>
              <span>{typeof rec === 'string' ? rec : (rec.recommendation || rec.description || JSON.stringify(rec))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Helper Components ────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color }) {
  return (
    <div className="overview-stat-card">
      <span className="overview-stat-value" style={color ? { color } : {}}>{value}</span>
      <span className="overview-stat-label">{label}{sub ? <span className="overview-stat-sub"> {sub}</span> : ''}</span>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="kv-row">
      <span className="kv-label">{label}</span>
      <span className="kv-value">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
    </div>
  );
}

// ─── Main Page Component ──────────────────────────────────────────────────────

export default function AnalysisDetailPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalysis();
    // eslint-disable-next-line
  }, [submissionId]);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/analysis/${submissionId}`);
      if (!res.ok) throw new Error('Failed to fetch analysis results.');
      const json = await res.json();
      if (json.analysisStatus === 'failed') {
        setError(json.error || 'Analysis failed.');
      } else if (json.analysisStatus === 'none') {
        setError('No analysis has been run yet.');
      } else if (json.analysisStatus === 'analyzing' || json.analysisStatus === 'pending') {
        setError('Analysis is still running. Please check back shortly.');
      } else {
        setData(json);
      }
    } catch (err) {
      setError(err.message);
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-loader"><div className="spinner" /></div>
    );
  }

  if (error) {
    const isFailed = error.includes('Analysis error:') || error.includes('Internal error:') || error.includes('Analysis failed');
    return (
      <div className="analysis-detail-page">
        <div className="analysis-error">
          <h2>{isFailed ? 'Analysis Failed' : 'Analysis Not Available'}</h2>
          <p style={{ marginBottom: '0.75rem' }}>{error}</p>
          {isFailed && (
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
              This may be caused by a corrupted or unusually structured DXL export file. You can try re-exporting the DXL and uploading again, or contact our team for assistance.
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
            {isFailed && (
              <button className="btn btn-outline" onClick={() => { setError(null); setLoading(true); fetchAnalysis(); }}>
                Check Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.results) {
    return (
      <div className="analysis-detail-page">
        <div className="analysis-error">
          <h2>No Data</h2>
          <p>Analysis results are empty.</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const { summary, results } = data;

  return (
    <div className="analysis-detail-page">
      {/* Header */}
      <div className="analysis-detail-header">
        <button className="btn btn-ghost btn-back" onClick={() => navigate('/dashboard')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back to Dashboard
        </button>
        <h1>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          {summary ? summary.applicationName : 'Analysis'}
        </h1>
        {summary && (
          <div className="analysis-header-meta">
            <span className="meta-badge" style={{ color: complexityBadge(summary.complexityLabel) }}>{summary.complexityLabel} Complexity</span>
            <span className="meta-badge">{summary.totalElements} elements</span>
            <span className="meta-badge">{data.duration ? `${data.duration}ms` : ''}</span>
          </div>
        )}
      </div>

      {/* Tab navigation */}
      <div className="analysis-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`analysis-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.key === 'forms' && summary && <span className="tab-count">{summary.totalForms || 0}</span>}
            {tab.key === 'views' && summary && <span className="tab-count">{summary.totalViews || 0}</span>}
            {tab.key === 'agents' && summary && <span className="tab-count">{summary.totalAgents || 0}</span>}
            {tab.key === 'relationships' && summary && <span className="tab-count">{summary.totalRelationships || 0}</span>}
            {tab.key === 'workflows' && summary && <span className="tab-count">{summary.workflowCount || 0}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="analysis-tab-panel">
        {activeTab === 'overview' && <OverviewTab summary={summary} databaseInfo={results.databaseInfo} results={results} />}
        {activeTab === 'forms' && <FormsTab forms={results.forms} />}
        {activeTab === 'views' && <ViewsTab views={results.views} />}
        {activeTab === 'agents' && <AgentsTab results={results} />}
        {activeTab === 'relationships' && <RelationshipsTab relationships={results.relationships} />}
        {activeTab === 'workflows' && <WorkflowsTab workflows={results.workflows} validation={results.validation} consolidation={results.consolidation} junction={results.junction} domain={results.domain} />}
        {activeTab === 'security' && <SecurityTab security={results.security} />}
        {activeTab === 'sql' && <SQLTab sql={results.sql} />}
        {activeTab === 'migration' && <MigrationTab migration={results.migration} complexity={results.complexity} />}
      </div>
    </div>
  );
}
